import './App.css'
import React, { useState, useEffect } from 'react'
import PDFViewer from './PDFViewer'
import LazyImage from './LazyImage'

interface Album {
  id: string
  name: string
  description: string
  coverImage: string
  photoCount: number
  createdDate: string
  hidden?: boolean
}

interface Photo {
  id: string
  albumId: string
  title: string
  url: string
  thumbnailUrl?: string
  caption: string
  uploadDate: string
  type?: 'image' | 'video' | 'pdf'
  focalPoint?: string | null
}

interface PhotoManagerProps {
  albums: Album[]
  setAlbums: React.Dispatch<React.SetStateAction<Album[]>>
  photos: Photo[]
  setPhotos: React.Dispatch<React.SetStateAction<Photo[]>>
  onAlbumsChange?: () => void
}

// Lambda Backend Configuration
const BACKEND_URL = (typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://8vosl9zwo9.execute-api.us-east-1.amazonaws.com')

// Extracts the S3 object filename from a photo URL, matching how review events are keyed on the backend
function getFilenameFromUrl (url: string): string {
  try {
    const decodedPath = decodeURIComponent(new URL(url).pathname)
    return decodedPath.split('/').pop() || url
  } catch {
    return url
  }
}

function PhotoManager ({ albums, setAlbums, photos, setPhotos, onAlbumsChange }: PhotoManagerProps): JSX.Element | null {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [authToken, setAuthToken] = useState<string | null>(null)
  const [view, setView] = useState<'albums' | 'photos' | 'statistics'>('albums')
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null)
  const selectedAlbum = selectedAlbumId ? albums.find(a => a.id === selectedAlbumId) || null : null
  const [isLoading, setIsLoading] = useState(false)

  const [newAlbumName, setNewAlbumName] = useState('')
  const [newAlbumDesc, setNewAlbumDesc] = useState('')
  const [showNewAlbumForm, setShowNewAlbumForm] = useState(false)
  const [editingAlbumId, setEditingAlbumId] = useState<string | null>(null)
  const [editingAlbumName, setEditingAlbumName] = useState('')
  const [editingAlbumDesc, setEditingAlbumDesc] = useState('')
  const [uploadMessage, setUploadMessage] = useState('')
  
  // Drag and drop state for reordering albums
  const [draggedAlbumId, setDraggedAlbumId] = useState<string | null>(null)
  const [dragOverAlbumId, setDragOverAlbumId] = useState<string | null>(null)
  const [localAlbums, setLocalAlbums] = useState<Album[]>([])

  // Drag and drop state for reordering photos
  const [draggedPhotoId, setDraggedPhotoId] = useState<string | null>(null)
  const [dragOverPhotoId, setDragOverPhotoId] = useState<string | null>(null)
  const [dragOverAlbumIdForPhoto, setDragOverAlbumIdForPhoto] = useState<string | null>(null)
  const [draggedFromAlbumId, setDraggedFromAlbumId] = useState<string | null>(null)
  const [isUploadingRef] = useState({ current: false })
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set())
  const [renamingPhotoId, setRenamingPhotoId] = useState<string | null>(null)
  const [renamingPhotoTitle, setRenamingPhotoTitle] = useState('')
  const [togglingAlbumId, setTogglingAlbumId] = useState<string | null>(null)
  const [albumOrderModified, setAlbumOrderModified] = useState(false)

  // Maximize photo display state
  const [maximizedPhotoId, setMaximizedPhotoId] = useState<string | null>(null)
  const maximizedPhoto = maximizedPhotoId ? photos.find(p => p.id === maximizedPhotoId) : null
  const [photoZoom, setPhotoZoom] = useState(100)
  const [isModalMinimized, setIsModalMinimized] = useState(false)
  const [galleryZoom, setGalleryZoom] = useState(100)

  // Watermark state
  const [watermarkingPhotoId, setWatermarkingPhotoId] = useState<string | null>(null)
  const [watermarkText, setWatermarkText] = useState('© eqsong')
  const [watermarkPosition, setWatermarkPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center'>('bottom-right')
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.7)
  const [watermarkSize, setWatermarkSize] = useState(30)

  // Hero position (focal point) editor state - lets admin pick the best center for the full-width home page hero image
  const [heroPositionPhotoId, setHeroPositionPhotoId] = useState<string | null>(null)
  const [heroFocalX, setHeroFocalX] = useState(50)
  const [heroFocalY, setHeroFocalY] = useState(20)

  // Hero window height editor state - lets admin resize the home page hero section
  const [heroHeightModalOpen, setHeroHeightModalOpen] = useState(false)
  const [heroHeightValue, setHeroHeightValue] = useState(700)
  const [heroHeightLoading, setHeroHeightLoading] = useState(false)

  // Statistics state
  const [statisticsData, setStatisticsData] = useState<Array<{albumId: string, albumName: string, filename: string, count: number}>>([])
  const [statisticsLoading, setStatisticsLoading] = useState(false)
  const [eventsModalOpen, setEventsModalOpen] = useState(false)
  const [eventsLoading, setEventsLoading] = useState(false)
  const [eventsForPhoto, setEventsForPhoto] = useState<string[]>([])
  const [eventsPhotoInfo, setEventsPhotoInfo] = useState<{ albumId: string, filename: string } | null>(null)

  // Daily review chart state (selectable period, up to 2 years)
  const [dailyChartData, setDailyChartData] = useState<Array<{ date: string, label: string, count: number }>>([])
  const [dailyChartDetails, setDailyChartDetails] = useState<Record<string, Photo[]>>({})
  const [dailyChartLoading, setDailyChartLoading] = useState(false)
  const [selectedChartDate, setSelectedChartDate] = useState<string | null>(null)
  const [dailyChartRangeDays, setDailyChartRangeDays] = useState(30)
  const [previewPhoto, setPreviewPhoto] = useState<Photo | null>(null)

  const CHART_RANGE_OPTIONS: Array<{ label: string, days: number }> = [
    { label: '7D', days: 7 },
    { label: '30D', days: 30 },
    { label: '90D', days: 90 },
    { label: '6M', days: 182 },
    { label: '1Y', days: 365 },
    { label: '2Y', days: 730 }
  ]

  // Load token from localStorage on mount
  useEffect(() => {
    console.log(`🔌 PhotoManager using backend: ${BACKEND_URL}`)
    const savedToken = localStorage.getItem('photoManagerToken')
    const tokenExpiry = localStorage.getItem('photoManagerTokenExpiry')
    if (savedToken && tokenExpiry && new Date().getTime() < parseInt(tokenExpiry)) {
      setAuthToken(savedToken)
      setIsAuthenticated(true)
      // Load albums with the token directly
      void loadAlbumsAndPhotos(savedToken)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load albums and photos from Lambda backend
  const loadAlbumsAndPhotos = async (token?: string): Promise<void> => {
    try {
      setIsLoading(true)
      const tokenToUse = token || authToken
      console.log(`📚 Loading albums with token: ${tokenToUse?.substring(0, 30)}...`)
      console.log(`📚 Fetching from: ${BACKEND_URL}/api/albums`)
      const response = await fetch(`${BACKEND_URL}/api/albums`, {
        headers: tokenToUse ? { 'Authorization': `Bearer ${tokenToUse}` } : {}
      })
      console.log(`📚 Albums response: ${response.status}`)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error(`📚 Albums error (${response.status}): ${errorText}`)
        setUploadMessage(`❌ Failed to load albums - ${response.status}. Backend: ${BACKEND_URL}`)
        setTimeout(() => setUploadMessage(''), 3000)
        return
      }
      
      const data = await response.json()
      console.log(`📚 Got albums data:`, data)
      // Backend returns { albums: [...] } - extract the array
      const backendAlbums = Array.isArray(data) ? data : (data.albums || [])
      console.log(`📚 Found ${backendAlbums.length} albums`)
      
      // Fetch photos for each album
      const allPhotos: Photo[] = []
      for (const album of backendAlbums) {
        const albumId = typeof album === 'string' ? album : album.id
        console.log(`📚 Fetching photos for album: ${albumId}`)
        const photosResponse = await fetch(`${BACKEND_URL}/api/photos/${albumId}`, {
          headers: tokenToUse ? { 'Authorization': `Bearer ${tokenToUse}` } : {}
        })
        if (photosResponse.ok) {
          const albumPhotosData = await photosResponse.json()
          // Backend returns { photos: [...] } - extract the array
          const albumPhotos = Array.isArray(albumPhotosData) ? albumPhotosData : (albumPhotosData.photos || [])
          console.log(`📚 Album ${albumId} has ${albumPhotos.length} photos`)
          const transformedPhotos = albumPhotos.map((photo: any) => ({
            id: photo.id || photo.key || photo.name,
            albumId: albumId,
            title: photo.title || photo.name,
            url: photo.url,
            caption: photo.caption || photo.name,
            uploadDate: photo.uploadDate || (photo.lastModified ? new Date(photo.lastModified).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
            type: photo.type || 'image' as const
          }))
          allPhotos.push(...transformedPhotos)
        } else {
          console.warn(`📚 Failed to fetch photos for ${albumId}: ${photosResponse.status}`)
        }
      }
      
      // Transform albums to match frontend Album interface
      const transformedAlbums = backendAlbums.map((album: any) => {
        const albumId = typeof album === 'string' ? album : album.id
        const albumName = typeof album === 'string' ? album : (album.name || album.id)
        const nameStr = String(albumName || '').trim() // Ensure it's a string and handle null/undefined
        const displayName = nameStr.length > 0 ? (nameStr.charAt(0).toUpperCase() + nameStr.slice(1)) : 'Unnamed Album'
        const albumPhotos = allPhotos.filter(p => p.albumId === albumId)
        // Use first non-video, non-pdf image as cover, or first photo's thumbnail if available
        const coverPhoto = albumPhotos.find((p: any) => p.type !== 'video' && p.type !== 'pdf') || albumPhotos[0]
        return {
          id: albumId,
          name: displayName,
          description: typeof album === 'string' ? '' : (album.description || ''),
          coverImage: coverPhoto?.thumbnailUrl || coverPhoto?.url || 'https://via.placeholder.com/300',
          photoCount: albumPhotos.length,
          createdDate: new Date().toISOString().split('T')[0]
        }
      })
      
      console.log(`📚 Successfully loaded ${transformedAlbums.length} albums with ${allPhotos.length} photos`)
      
      // Preserve local album order if it exists and has the same albums
      let finalAlbums = transformedAlbums
      if (localAlbums.length === transformedAlbums.length) {
        // If we have a local order with the same number of albums, use it
        const localAlbumIds = localAlbums.map((a: Album) => a.id)
        const newAlbumIds = transformedAlbums.map((a: Album) => a.id)
        const isSameAlbums = localAlbumIds.every(id => newAlbumIds.includes(id))
        
        if (isSameAlbums) {
          // Reorder the new albums according to the local order
          finalAlbums = localAlbumIds.map((id: string) => transformedAlbums.find((a: Album) => a.id === id)!).filter(Boolean)
          console.log(`📚 Preserving local album order`)
        }
      }
      
      setAlbums(finalAlbums)
      setLocalAlbums(finalAlbums)
      setPhotos(allPhotos)
    } catch (err) {
      console.error('Failed to load albums and photos:', err)
      setUploadMessage('❌ Failed to load albums')
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogin = async (): Promise<void> => {
    if (!passwordInput) {
      setPasswordError('❌ Password is required')
      return
    }

    try {
      setIsLoading(true)
      console.log(`🔐 Logging in at ${BACKEND_URL}/api/login`)
      // Get JWT token from backend
      const loginResponse = await fetch(`${BACKEND_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: passwordInput })
      })

      console.log(`🔐 Login response status: ${loginResponse.status}`)
      
      if (loginResponse.ok) {
        const loginData = await loginResponse.json()
        const jwtToken = loginData.token
        console.log(`✅ Got JWT token: ${jwtToken?.substring(0, 50)}...`)
        
        // Save JWT token to localStorage
        localStorage.setItem('photoManagerToken', jwtToken)
        localStorage.setItem('photoManagerTokenExpiry', (new Date().getTime() + 8 * 60 * 60 * 1000).toString())
        
        setAuthToken(jwtToken)
        setIsAuthenticated(true)
        setPasswordInput('')
        setPasswordError('')
        
        // Load albums and photos with the new token
        await loadAlbumsAndPhotos(jwtToken)
      } else {
        const errorData = await loginResponse.json()
        console.error(`❌ Login error:`, errorData)
        setPasswordError(`❌ ${errorData.error || 'Login failed'}`)
        setPasswordInput('')
      }
    } catch (err) {
      console.error('🔐 Login error:', err)
      setPasswordError('❌ Login failed')
      setPasswordInput('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = (): void => {
    localStorage.removeItem('photoManagerToken')
    localStorage.removeItem('photoManagerTokenExpiry')
    setAuthToken(null)
    setIsAuthenticated(false)
    setView('albums')
    setSelectedAlbumId(null)
  }

  const handleCreateAlbum = async (): Promise<void> => {
    if (!newAlbumName.trim()) {
      setUploadMessage('⚠️ Album name is required.')
      return
    }

    try {
      setIsLoading(true)
      console.log(`Creating album: ${newAlbumName} at ${BACKEND_URL}/api/albums`)
      console.log(`Auth token: ${authToken?.substring(0, 20)}...`)
      
      const response = await fetch(`${BACKEND_URL}/api/albums`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: newAlbumName, description: newAlbumDesc })
      })

      console.log(`Response status: ${response.status}`)
      
      if (response.ok) {
        const data = await response.json()
        console.log('Album created:', data)
        setUploadMessage('✅ Album created successfully!')
        setNewAlbumName('')
        setNewAlbumDesc('')
        setShowNewAlbumForm(false)
        await loadAlbumsAndPhotos()
        setTimeout(() => setUploadMessage(''), 3000)
        
        // Notify Gallery to refresh
        if (onAlbumsChange) {
          console.log('📢 PhotoManager: Notifying Gallery to refresh after create')
          onAlbumsChange()
        }
      } else {
        const errorData = await response.json()
        console.error('Album creation error:', errorData)
        setUploadMessage(`❌ Failed to create album: ${errorData.error || response.statusText}`)
        setTimeout(() => setUploadMessage(''), 5000)
      }
    } catch (err) {
      console.error('Create album error:', err)
      setUploadMessage(`❌ Error creating album: ${err instanceof Error ? err.message : String(err)}`)
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAlbum = async (albumToDelete?: Album): Promise<void> => {
    // Use provided album or fall back to selectedAlbum
    const albumForDeletion = albumToDelete || selectedAlbum
    console.log(`🗑️ Delete handler called. albumToDelete: ${albumToDelete ? albumToDelete.name : 'not provided'}, selectedAlbumId: ${selectedAlbumId}, selectedAlbum: ${selectedAlbum ? selectedAlbum.name : 'null'}, albums count: ${albums.length}`)
    
    if (!albumForDeletion) {
      console.error('🗑️ No album selected or provided')
      return
    }
    
    if (!authToken) {
      console.error('🗑️ No auth token available')
      setUploadMessage('❌ Authentication required. Please log in.')
      return
    }
    
    if (!window.confirm(`Delete album "${albumForDeletion.name}"? This cannot be undone.`)) return

    try {
      setIsLoading(true)
      console.log(`🗑️ Deleting album: ${albumForDeletion.id} with token: ${authToken.substring(0, 20)}...`)
      const response = await fetch(`${BACKEND_URL}/api/albums/${albumForDeletion.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      })

      console.log(`🗑️ Delete response: ${response.status}`)
      if (response.ok) {
        setUploadMessage('✅ Album deleted successfully!')
        await loadAlbumsAndPhotos(authToken)
        setView('albums')
        setSelectedAlbumId(null)
        setTimeout(() => setUploadMessage(''), 3000)
        
        // Notify Gallery to refresh
        if (onAlbumsChange) {
          console.log('📢 PhotoManager: Notifying Gallery to refresh after delete')
          onAlbumsChange()
        }
      } else {
        const errorText = await response.text()
        console.error(`🗑️ Delete error (${response.status}): ${errorText}`)
        setUploadMessage(`❌ Failed to delete album (${response.status})`)
        setTimeout(() => setUploadMessage(''), 3000)
      }
    } catch (err) {
      console.error('🗑️ Delete album error:', err)
      setUploadMessage(`❌ Error deleting album: ${err instanceof Error ? err.message : String(err)}`)
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleStartEditAlbum = (album: Album): void => {
    setEditingAlbumId(album.id)
    setEditingAlbumName(album.name)
    setEditingAlbumDesc(album.description)
  }

  const handleSaveAlbum = async (): Promise<void> => {
    if (!editingAlbumId || !editingAlbumName.trim()) {
      setUploadMessage('⚠️ Album name is required.')
      return
    }

    try {
      setIsLoading(true)
      const response = await fetch(`${BACKEND_URL}/api/albums/${editingAlbumId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          name: editingAlbumName,
          description: editingAlbumDesc 
        })
      })

      if (response.ok) {
        setUploadMessage('✅ Album updated successfully!')
        setEditingAlbumId(null)
        await loadAlbumsAndPhotos()
        setTimeout(() => setUploadMessage(''), 3000)
        
        // Notify Gallery to refresh
        if (onAlbumsChange) {
          console.log('📢 PhotoManager: Notifying Gallery to refresh after save')
          onAlbumsChange()
        }
      } else {
        setUploadMessage('❌ Failed to update album')
        setTimeout(() => setUploadMessage(''), 3000)
      }
    } catch (err) {
      console.error('Save album error:', err)
      setUploadMessage('❌ Error updating album')
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileUpload = async (files: FileList | null): Promise<void> => {
    if (!files || !selectedAlbum || !authToken) {
      console.warn('📤 Upload missing required params:', { hasFiles: !!files, hasAlbum: !!selectedAlbum, hasAuth: !!authToken })
      return
    }

    // Prevent duplicate uploads - check if already uploading
    if (isLoading) {
      console.warn('📤 Upload already in progress, ignoring duplicate upload request')
      return
    }

    // Maximum file size: Lambda has 6MB payload limit, but we support up to 760MB via presigned URLs
    const LAMBDA_MAX_FILE_SIZE = 4 * 1024 * 1024 // 4MB for direct Lambda upload (safer margin)
    const PRESIGNED_MAX_FILE_SIZE = 760 * 1024 * 1024 // 760MB for presigned S3 URLs
    
    // Validate file sizes
    for (const file of Array.from(files)) {
      if (file.size > PRESIGNED_MAX_FILE_SIZE) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2)
        setUploadMessage(`❌ File "${file.name}" is too large (${sizeMB}MB). Maximum size is 760MB.`)
        setTimeout(() => setUploadMessage(''), 5000)
        return
      }
    }

    try {
      setIsLoading(true)
      setUploadMessage('📤 Uploading...')
      const fileArray = Array.from(files)
      console.log(`📤 Uploading ${fileArray.length} file(s) to album ${selectedAlbum.id}`)

      // Split files: small (Lambda) and large (presigned S3 URLs)
      const largeFiles = fileArray.filter(f => f.size > LAMBDA_MAX_FILE_SIZE)
      const smallFiles = fileArray.filter(f => f.size <= LAMBDA_MAX_FILE_SIZE)
      
      const uploadPromises: Promise<any>[] = []
      let presignedUrls: any[] = []

      // Upload small files through Lambda
      if (smallFiles.length > 0) {
        console.log(`📤 Uploading ${smallFiles.length} small file(s) through Lambda (< 5.5MB)`)
        const smallUploadPromises = smallFiles.map(async (file) => {
          console.log(`📤 Uploading small file: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB)`)
          const formData = new FormData()
          formData.append('photos', file)

          try {
            const response = await fetch(`${BACKEND_URL}/api/photos/${selectedAlbum.id}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`
              },
              body: formData
            })

            if (!response.ok) {
              const errorText = await response.text()
              console.error(`📤 Upload failed for ${file.name}: ${errorText}`)
              throw new Error(`Upload failed: ${response.status} - ${errorText}`)
            }
            const result = await response.json()
            console.log(`📤 Upload success for ${file.name}:`, result)
            return result
          } catch (fetchErr) {
            console.error(`📤 Fetch error for ${file.name}:`, fetchErr)
            throw fetchErr
          }
        })
        uploadPromises.push(...smallUploadPromises)
      }

      // Upload large files via presigned URLs
      if (largeFiles.length > 0) {
        console.log(`📤 Uploading ${largeFiles.length} large file(s) via presigned S3 URLs (> 5.5MB)`)
        
        try {
          // Get presigned URLs from backend
          const presignedResponse = await fetch(`${BACKEND_URL}/api/presigned-urls/${selectedAlbum.id}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              files: largeFiles.map(f => ({ name: f.name, size: f.size, type: f.type }))
            })
          })

          if (!presignedResponse.ok) {
            const errorText = await presignedResponse.text()
            throw new Error(`Failed to get presigned URLs: ${presignedResponse.status} - ${errorText}`)
          }

          const presignedData = await presignedResponse.json()
          presignedUrls = presignedData.presignedUrls
          console.log(`📤 Got ${presignedUrls.length} presigned URLs for direct S3 upload`)

          // Upload each large file directly to S3 using presigned URL
          const largeUploadPromises = largeFiles.map(async (file, index) => {
            const presignedInfo = presignedUrls[index]
            console.log(`📤 Uploading large file to S3: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB)`)

            try {
              const s3Response = await fetch(presignedInfo.presignedUrl, {
                method: 'PUT',
                headers: {
                  'Content-Type': file.type
                },
                body: file
              })

              if (!s3Response.ok) {
                throw new Error(`S3 upload failed: ${s3Response.status}`)
              }

              console.log(`📤 Large file uploaded to S3: ${file.name}`)
              // Return metadata for the uploaded file
              return {
                message: 'File uploaded successfully!',
                photos: [{
                  id: presignedInfo.key,
                  albumId: selectedAlbum.id,
                  title: file.name,
                  url: presignedInfo.s3Url,
                  uploadDate: new Date().toISOString().split('T')[0],
                  type: file.type.startsWith('video/') ? 'video' : (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) ? 'pdf' : 'image'
                }]
              }
            } catch (s3Err) {
              console.error(`📤 S3 upload error for ${file.name}:`, s3Err)
              throw s3Err
            }
          })
          uploadPromises.push(...largeUploadPromises)
        } catch (presignedErr) {
          console.error(`📤 Presigned URL error:`, presignedErr)
          throw new Error(`Failed to get upload URLs: ${presignedErr instanceof Error ? presignedErr.message : String(presignedErr)}`)
        }
      }

      const results = await Promise.all(uploadPromises)
      console.log(`📤 All uploads complete. Results:`, results)
      // Backend returns {message: '...', photos: [...]} - check if response has message or photos
      const successCount = results.filter((r: any) => r.message || r.photos || r.success || r.key).length
      console.log(`📤 Success count: ${successCount}/${results.length}`)
      
      if (successCount > 0) {
        // Generate thumbnails for large files uploaded via presigned URLs
        if (largeFiles.length > 0) {
          try {
            console.log(`🖼️ Generating thumbnails for ${largeFiles.length} large file(s)`)
            const filenames = presignedUrls.map((p: any) => p.key.split('/').pop())
            const thumbnailResponse = await fetch(`${BACKEND_URL}/api/thumbnails/generate/${selectedAlbum.id}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ filenames })
            })
            
            if (thumbnailResponse.ok) {
              const thumbnailResult = await thumbnailResponse.json()
              console.log(`🖼️ Thumbnail generation complete:`, thumbnailResult)
            } else {
              console.warn(`🖼️ Thumbnail generation failed: ${thumbnailResponse.status}`)
            }
          } catch (thumbErr) {
            console.warn(`🖼️ Thumbnail generation error (non-critical):`, thumbErr)
          }
        }
        
        // Reload albums and photos
        console.log(`📤 Reloading albums and photos after ${successCount} successful uploads`)
        await loadAlbumsAndPhotos(authToken || '')
        
        setUploadMessage(`✅ Uploaded ${successCount} photo(s)!`)
        setTimeout(() => setUploadMessage(''), 3000)
      } else {
        console.error('📤 No successful uploads')
        setUploadMessage('❌ Upload failed')
        setTimeout(() => setUploadMessage(''), 3000)
      }
    } catch (err) {
      console.error('Upload error:', err)
      setUploadMessage(`❌ Failed to upload photos: ${err instanceof Error ? err.message : String(err)}`)
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteMultiplePhotos = async (): Promise<void> => {
    if (selectedPhotoIds.size === 0) return
    if (!window.confirm(`Delete ${selectedPhotoIds.size} photo(s)? This cannot be undone.`)) return

    try {
      setIsLoading(true)
      let successCount = 0
      const photoIdsArray = Array.from(selectedPhotoIds)
      
      for (const photoId of photoIdsArray) {
        try {
          // photoId format: "albumId/path/to/file.jpg"
          // Extract everything after the first "/" (the relative path within the album)
          const albumPrefix = `${selectedAlbum?.id}/`
          const relativePath = photoId.startsWith(albumPrefix) 
            ? photoId.substring(albumPrefix.length)
            : (photoId.includes('/') ? photoId.split('/').slice(1).join('/') : photoId)
          
          const response = await fetch(`${BACKEND_URL}/api/photos/${selectedAlbum?.id}/${encodeURIComponent(relativePath)}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          })
          if (response.ok) {
            successCount++
          }
        } catch (err) {
          console.error(`🗑️ Error deleting photo:`, err)
        }
      }

      if (successCount > 0) {
        setUploadMessage(`✅ Deleted ${successCount} photo(s)!`)
        setSelectedPhotoIds(new Set())
        await loadAlbumsAndPhotos(authToken || '')
        setTimeout(() => setUploadMessage(''), 3000)
      } else {
        setUploadMessage('❌ Failed to delete photos')
        setTimeout(() => setUploadMessage(''), 3000)
      }
    } catch (err) {
      console.error('Error deleting photos:', err)
      setUploadMessage('❌ Failed to delete photos')
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRenamePhoto = async (photoId: string): Promise<void> => {
    if (!renamingPhotoTitle.trim()) {
      setUploadMessage('⚠️ Photo name cannot be empty.')
      return
    }

    try {
      setIsLoading(true)
      const filename = photoId.includes('/') ? photoId.split('/').pop() : photoId
      
      console.log('🔄 handleRenamePhoto called:', {
        photoId,
        filename,
        newTitle: renamingPhotoTitle,
        albumId: selectedAlbum?.id
      })
      
      // Update the photo title in the frontend state first
      const updatedPhotos = photos.map(p => 
        p.id === photoId ? { ...p, title: renamingPhotoTitle } : p
      )
      setPhotos(updatedPhotos)
      
      // Try to update on backend (optional - if backend supports it)
      const putUrl = `${BACKEND_URL}/api/photos/${selectedAlbum?.id}/${filename}`
      console.log('📤 Sending PUT request to:', putUrl)
      console.log('📋 Request body:', { title: renamingPhotoTitle })
      console.log('🔐 Auth token exists:', !!authToken)
      
      const response = await fetch(putUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: renamingPhotoTitle })
      })

      console.log('📥 Response status:', response.status)
      const responseData = await response.json()
      console.log('📥 Response data:', responseData)

      if (response.ok) {
        console.log('✅ Backend confirm: Photo metadata saved')
        setUploadMessage('✅ Photo renamed successfully!')
        
        // Reload photos from backend to ensure persistence
        console.log('🔄 Reloading photos to confirm persistence...')
        const photosResponse = await fetch(`${BACKEND_URL}/api/photos/${selectedAlbum?.id}`)
        if (photosResponse.ok) {
          const photosData = await photosResponse.json()
          const reloadedPhotos = Array.isArray(photosData) ? photosData : (photosData.photos || [])
          console.log(`✅ Reloaded ${reloadedPhotos.length} photos from backend`)
          
          // Transform and update state
          const transformedPhotos = reloadedPhotos.map((photo: any) => ({
            id: photo.id,
            albumId: photo.albumId,
            title: photo.title,
            caption: photo.caption || '',
            url: photo.url,
            uploadDate: photo.uploadDate,
            type: photo.type || 'image'
          }))
          
          // Update only the photos from this album
          const otherPhotos = photos.filter(p => p.albumId !== selectedAlbum?.id)
          setPhotos([...otherPhotos, ...transformedPhotos])
          console.log(`✅ Updated state with reloaded photos`)
        }
      } else {
        console.warn('⚠️ Backend error response:', responseData)
        // Photo title updated in UI regardless
        setUploadMessage('✅ Photo title updated!')
      }
      
      setRenamingPhotoId(null)
      setRenamingPhotoTitle('')
      setTimeout(() => setUploadMessage(''), 3000)
    } catch (err) {
      console.error('❌ Error renaming photo:', err)
      setUploadMessage('✅ Photo title updated!')
      setRenamingPhotoId(null)
      setRenamingPhotoTitle('')
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleApplyWatermark = async (photoId: string): Promise<void> => {
    console.log('🎨 handleApplyWatermark CALLED with photoId:', photoId)
    
    if (!watermarkText.trim()) {
      console.log('⚠️ Empty watermark text')
      setUploadMessage('⚠️ Watermark text cannot be empty.')
      return
    }

    if (!selectedAlbum?.id) {
      console.log('⚠️ No album selected:', selectedAlbum)
      setUploadMessage('⚠️ No album selected')
      return
    }

    if (!authToken) {
      console.log('⚠️ No auth token')
      setUploadMessage('⚠️ Not authenticated')
      return
    }

    try {
      setIsLoading(true)
      const filename = photoId.includes('/') ? photoId.split('/').pop() : photoId
      
      if (!filename) {
        console.log('⚠️ Unable to extract filename from photoId:', photoId)
        setUploadMessage('⚠️ Unable to extract filename')
        return
      }
      
      console.log('🎨 Applying watermark:', {
        photoId,
        filename,
        watermarkText,
        watermarkPosition,
        watermarkOpacity,
        watermarkSize,
        albumId: selectedAlbum?.id,
        hasAuthToken: !!authToken
      })

      // Apply watermark via backend - encode filename for URLs with spaces/special chars
      const encodedFilename = encodeURIComponent(filename)
      const watermarkUrl = `${BACKEND_URL}/api/photos/${selectedAlbum.id}/${encodedFilename}/watermark`
      console.log('📤 Watermark endpoint:', watermarkUrl)
      
      const response = await fetch(watermarkUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: watermarkText,
          position: watermarkPosition,
          opacity: watermarkOpacity,
          fontSize: watermarkSize
        })
      })

      console.log('📥 Response status:', response.status)

      if (response.ok) {
        const result = await response.json()
        console.log('✅ Watermark applied successfully:', result)
        setUploadMessage('✅ Watermark applied successfully!')
        setWatermarkingPhotoId(null)
        
        // Reload photos to show updated image
        const photosResponse = await fetch(`${BACKEND_URL}/api/photos/${selectedAlbum.id}`)
        if (photosResponse.ok) {
          const photosData = await photosResponse.json()
          const reloadedPhotos = Array.isArray(photosData) ? photosData : (photosData.photos || [])
          const transformedPhotos = reloadedPhotos.map((photo: any) => ({
            id: photo.id,
            albumId: photo.albumId,
            title: photo.title,
            caption: photo.caption || '',
            url: photo.url,
            uploadDate: photo.uploadDate,
            type: photo.type || 'image'
          }))
          const otherPhotos = photos.filter(p => p.albumId !== selectedAlbum?.id)
          setPhotos([...otherPhotos, ...transformedPhotos])
          console.log('✅ Updated photos after watermark')
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.warn('⚠️ Watermark error:', errorData)
        setUploadMessage(`❌ Failed to apply watermark: ${errorData.error || 'Unknown error'}`)
      }
      
      setTimeout(() => setUploadMessage(''), 5000)
    } catch (err) {
      console.error('❌ Error applying watermark:', err)
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      setUploadMessage(`❌ Error: ${errorMsg}`)
      setTimeout(() => setUploadMessage(''), 5000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUndoWatermark = async (photoId: string): Promise<void> => {
    if (!selectedAlbum?.id) {
      setUploadMessage('⚠️ No album selected')
      return
    }

    if (!authToken) {
      setUploadMessage('⚠️ Not authenticated')
      return
    }

    try {
      setIsLoading(true)
      const filename = photoId.includes('/') ? photoId.split('/').pop() : photoId
      
      if (!filename) {
        console.log('⚠️ Unable to extract filename from photoId:', photoId)
        setUploadMessage('⚠️ Unable to extract filename')
        return
      }
      
      console.log('↩️ Undoing watermark:', {
        photoId,
        filename,
        albumId: selectedAlbum?.id
      })

      // URL encode the filename to handle spaces and special characters
      const encodedFilename = encodeURIComponent(filename)
      const undoUrl = `${BACKEND_URL}/api/photos/${selectedAlbum.id}/${encodedFilename}/undo-watermark`
      console.log('📤 Undo endpoint:', undoUrl)
      
      const response = await fetch(undoUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('📥 Undo response status:', response.status)

      if (response.ok) {
        const result = await response.json()
        console.log('✅ Watermark undone successfully:', result)
        setUploadMessage('✅ Watermark removed successfully!')
        
        // Reload photos to show updated image
        const photosResponse = await fetch(`${BACKEND_URL}/api/photos/${selectedAlbum.id}`)
        if (photosResponse.ok) {
          const photosData = await photosResponse.json()
          const reloadedPhotos = Array.isArray(photosData) ? photosData : (photosData.photos || [])
          const transformedPhotos = reloadedPhotos.map((photo: any) => ({
            id: photo.id,
            albumId: photo.albumId,
            title: photo.title,
            caption: photo.caption || '',
            url: photo.url,
            uploadDate: photo.uploadDate,
            type: photo.type || 'image'
          }))
          const otherPhotos = photos.filter(p => p.albumId !== selectedAlbum?.id)
          setPhotos([...otherPhotos, ...transformedPhotos])
          console.log('✅ Updated photos after undo')
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.warn('⚠️ Undo error:', errorData)
        setUploadMessage(`❌ ${errorData.error || 'Failed to undo watermark'}`)
      }
      
      setTimeout(() => setUploadMessage(''), 5000)
    } catch (err) {
      console.error('❌ Error undoing watermark:', err)
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      setUploadMessage(`❌ Error: ${errorMsg}`)
      setTimeout(() => setUploadMessage(''), 5000)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveHeroPosition = async (photoId: string): Promise<void> => {
    if (!selectedAlbum?.id) {
      setUploadMessage('⚠️ No album selected')
      return
    }

    if (!authToken) {
      setUploadMessage('⚠️ Not authenticated')
      return
    }

    const filename = photoId.includes('/') ? photoId.split('/').pop() : photoId
    if (!filename) {
      setUploadMessage('⚠️ Unable to extract filename')
      return
    }

    const focalPoint = `${heroFocalX}% ${heroFocalY}%`

    try {
      setIsLoading(true)
      const encodedFilename = encodeURIComponent(filename)
      const response = await fetch(`${BACKEND_URL}/api/photos/${selectedAlbum.id}/${encodedFilename}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ focalPoint })
      })

      if (response.ok) {
        setUploadMessage('✅ Hero position saved!')
        setPhotos(photos.map(p => p.id === photoId ? { ...p, focalPoint } : p))
        setHeroPositionPhotoId(null)
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setUploadMessage(`❌ Failed to save hero position: ${errorData.error || 'Unknown error'}`)
      }
      setTimeout(() => setUploadMessage(''), 3000)
    } catch (err) {
      console.error('❌ Error saving hero position:', err)
      setUploadMessage('❌ Failed to save hero position')
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const openHeroHeightModal = async (): Promise<void> => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/hero`)
      if (response.ok) {
        const data = await response.json()
        setHeroHeightValue(data.heroHeight || 700)
      }
    } catch (err) {
      console.log('Could not load current hero height, using default')
    }
    setHeroHeightModalOpen(true)
  }

  const handleSaveHeroHeight = async (): Promise<void> => {
    if (!authToken) {
      setUploadMessage('⚠️ Not authenticated')
      return
    }

    try {
      setHeroHeightLoading(true)
      const response = await fetch(`${BACKEND_URL}/api/settings/hero`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ heroHeight: heroHeightValue })
      })

      if (response.ok) {
        setUploadMessage('✅ Hero height saved!')
        setHeroHeightModalOpen(false)
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setUploadMessage(`❌ Failed to save hero height: ${errorData.error || 'Unknown error'}`)
      }
      setTimeout(() => setUploadMessage(''), 3000)
    } catch (err) {
      console.error('❌ Error saving hero height:', err)
      setUploadMessage('❌ Failed to save hero height')
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setHeroHeightLoading(false)
    }
  }

  const handleToggleAlbumHidden = async (albumId: string): Promise<void> => {
    const album = localAlbums.find(a => a.id === albumId)
    if (!album) return

    const isCurrentlyHidden = album.hidden || false
    const newHiddenStatus = !isCurrentlyHidden

    try {
      setTogglingAlbumId(albumId)
      setIsLoading(true)

      const response = await fetch(`${BACKEND_URL}/api/albums/${albumId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          name: album.name,
          description: album.description,
          hidden: newHiddenStatus
        })
      })

      if (response.ok) {
        const updatedAlbum = { ...album, hidden: newHiddenStatus }
        const updatedLocalAlbums = localAlbums.map(a => a.id === albumId ? updatedAlbum : a)
        setLocalAlbums(updatedLocalAlbums)
        setAlbums(updatedLocalAlbums)
        setUploadMessage(`✅ Album ${newHiddenStatus ? 'hidden' : 'shown'}!`)
        setTimeout(() => setUploadMessage(''), 3000)
        
        // Notify Gallery to refresh
        if (onAlbumsChange) {
          console.log('📢 PhotoManager: Notifying Gallery to refresh')
          onAlbumsChange()
        }
      } else {
        const errorText = await response.text()
        console.error(`❌ Toggle hidden failed: ${response.status} - ${errorText}`)
        setUploadMessage(`❌ Failed to ${newHiddenStatus ? 'hide' : 'show'} album`)
        setTimeout(() => setUploadMessage(''), 3000)
      }
    } catch (err) {
      console.error('Error toggling album hidden status:', err)
      setUploadMessage('❌ Failed to update album visibility')
      setTimeout(() => setUploadMessage(''), 3000)
    } finally {
      setTogglingAlbumId(null)
      setIsLoading(false)
    }
  }

  // Drag and drop handlers for album reordering
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, albumId: string): void => {
    setDraggedAlbumId(albumId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, albumId: string): void => {
    e.preventDefault()
    setDragOverAlbumId(albumId)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setDragOverAlbumId(null)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetAlbumId: string): void => {
    e.preventDefault()
    setDragOverAlbumId(null)

    if (!draggedAlbumId || draggedAlbumId === targetAlbumId) {
      return
    }

    // Reorder albums
    const draggedIndex = localAlbums.findIndex(a => a.id === draggedAlbumId)
    const targetIndex = localAlbums.findIndex(a => a.id === targetAlbumId)

    if (draggedIndex === -1 || targetIndex === -1) return

    const newAlbums = [...localAlbums]
    const [draggedAlbum] = newAlbums.splice(draggedIndex, 1)
    newAlbums.splice(targetIndex, 0, draggedAlbum)

    // Update local and parent state immediately for instant UI feedback
    setLocalAlbums(newAlbums)
    setAlbums(newAlbums)
    setAlbumOrderModified(true)
    setDraggedAlbumId(null)

    console.log(`📌 Album order changed: ${draggedAlbumId} moved to position ${targetIndex + 1}`)
    console.log(`📌 New album order:`, newAlbums.map(a => a.id))

    // Save album order to backend
    const albumIds = newAlbums.map(a => a.id)
    console.log(`📌 Saving order to backend:`, albumIds)
    
    fetch(`${BACKEND_URL}/api/albums/reorder`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ albumIds })
    })
      .then(async res => {
        console.log(`📌 Backend response status: ${res.status}`)
        const text = await res.text()
        console.log(`📌 Backend response body: ${text}`)
        try {
          return JSON.parse(text)
        } catch {
          return { success: res.ok, status: res.status }
        }
      })
      .then(data => {
        if (data.error) {
          console.error('❌ Failed to save album order:', data.error)
          setUploadMessage('❌ Failed to save album order (check console)')
          setTimeout(() => setUploadMessage(''), 3000)
        } else {
          console.log('✅ Album order saved to backend')
          setUploadMessage('✅ Album order updated!')
          setTimeout(() => setUploadMessage(''), 2000)
        }
      })
      .catch(err => {
        console.error('❌ Error saving album order:', err)
        setUploadMessage('❌ Error saving album order')
        setTimeout(() => setUploadMessage(''), 3000)
      })
  }

  // Load statistics data (review counts across all albums)
  const loadStatistics = async (token: string): Promise<void> => {
    setStatisticsLoading(true)
    try {
      const allStats: Array<{albumId: string, albumName: string, filename: string, count: number}> = []
      
      for (const album of albums) {
        const resp = await fetch(`${BACKEND_URL}/api/photos/${album.id}/review-counts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (resp.ok) {
          const data = await resp.json()
          const counts = data.counts || {}
          for (const [filename, count] of Object.entries(counts)) {
            if (count && Number(count) > 0) {
              allStats.push({
                albumId: album.id,
                albumName: album.name,
                filename: filename as string,
                count: Number(count)
              })
            }
          }
        }
      }
      
      // Sort by count descending
      allStats.sort((a, b) => b.count - a.count)
      setStatisticsData(allStats)
    } catch (err) {
      console.error('Failed to load statistics:', err)
    } finally {
      setStatisticsLoading(false)
    }
  }

  // Load view counts across all albums for the trend chart, bucketed by day/week/month depending on the selected period
  const loadDailyChart = async (token: string, days: number = dailyChartRangeDays): Promise<void> => {
    setDailyChartLoading(true)
    setSelectedChartDate(null)
    try {
      // Use daily buckets for short periods, weekly for up to ~6 months, monthly beyond that
      const granularity: 'day' | 'week' | 'month' = days <= 60 ? 'day' : days <= 200 ? 'week' : 'month'

      const bucketKeyFor = (d: Date): string => {
        if (granularity === 'day') return d.toISOString().slice(0, 10)
        if (granularity === 'week') {
          const weekStart = new Date(d)
          weekStart.setDate(weekStart.getDate() - weekStart.getDay())
          return weekStart.toISOString().slice(0, 10)
        }
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
      }
      const labelFor = (key: string): string => {
        const d = new Date(key)
        if (granularity === 'month') return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      }

      const bucketKeys: string[] = []
      const seenKeys = new Set<string>()
      const counts: Record<string, number> = {}
      const details: Record<string, Photo[]> = {}
      const now = new Date()
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now)
        d.setDate(d.getDate() - i)
        const key = bucketKeyFor(d)
        if (!seenKeys.has(key)) {
          seenKeys.add(key)
          bucketKeys.push(key)
          counts[key] = 0
          details[key] = []
        }
      }
      const rangeStart = new Date(now)
      rangeStart.setDate(rangeStart.getDate() - (days - 1))
      const seenPerBucket: Record<string, Set<string>> = {}
      bucketKeys.forEach(key => { seenPerBucket[key] = new Set() })

      for (const album of albums) {
        const resp = await fetch(`${BACKEND_URL}/api/photos/${album.id}/review-events`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (!resp.ok) continue
        const data = await resp.json()
        const events: Record<string, string[]> = data.events || {}
        for (const [filename, timestamps] of Object.entries(events)) {
          const matchedPhoto = photos.find(p => p.albumId === album.id && getFilenameFromUrl(p.url) === filename)
          timestamps.forEach(ts => {
            const eventDate = new Date(ts)
            if (eventDate < rangeStart || eventDate > now) return
            const key = bucketKeyFor(eventDate)
            if (!(key in counts)) return
            counts[key] += 1
            if (matchedPhoto && !seenPerBucket[key].has(matchedPhoto.id)) {
              seenPerBucket[key].add(matchedPhoto.id)
              details[key].push(matchedPhoto)
            }
          })
        }
      }

      const chartData = bucketKeys.map(key => ({
        date: key,
        label: labelFor(key),
        count: counts[key]
      }))
      setDailyChartData(chartData)
      setDailyChartDetails(details)
    } catch (err) {
      console.error('Failed to load daily review chart:', err)
    } finally {
      setDailyChartLoading(false)
    }
  }

  const loadReviewEvents = async (albumId: string, filename: string): Promise<void> => {
    try {
      setEventsLoading(true)
      setEventsForPhoto([])
      setEventsPhotoInfo({ albumId, filename })
      const resp = await fetch(`${BACKEND_URL}/api/photos/${albumId}/review-events`, {
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      })
      if (!resp.ok) {
        setEventsLoading(false)
        setEventsModalOpen(true)
        return
      }
      const data = await resp.json()
      const events = (data.events && data.events[filename]) || []
      events.sort((a: string, b: string) => (new Date(b).getTime() - new Date(a).getTime()))
      setEventsForPhoto(events)
      setEventsLoading(false)
      setEventsModalOpen(true)
    } catch (err) {
      console.error('Failed to load review events:', err)
      setEventsLoading(false)
      setEventsModalOpen(true)
    }
  }

  // Drag and drop handlers for photo reordering and moving between albums
  const handlePhotoCardDragStart = (e: React.DragEvent<HTMLDivElement>, photoId: string, albumId: string): void => {
    setDraggedPhotoId(photoId)
    setDraggedFromAlbumId(albumId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handlePhotoCardDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handlePhotoCardDragEnter = (e: React.DragEvent<HTMLDivElement>, photoId: string): void => {
    e.preventDefault()
    setDragOverPhotoId(photoId)
  }

  const handlePhotoCardDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setDragOverPhotoId(null)
  }

  const handlePhotoCardDrop = (e: React.DragEvent<HTMLDivElement>, targetPhotoId: string): void => {
    e.preventDefault()
    setDragOverPhotoId(null)

    if (!draggedPhotoId || !draggedFromAlbumId) return

    // Reorder photos within same album or move to different album
    const draggedAlbumPhotos = photos.filter(p => p.albumId === draggedFromAlbumId)
    const draggedIndex = draggedAlbumPhotos.findIndex(p => p.id === draggedPhotoId)

    const targetAlbumPhotos = photos.filter(p => p.albumId === selectedAlbum?.id)
    const targetIndex = targetAlbumPhotos.findIndex(p => p.id === targetPhotoId)

    if (draggedIndex === -1 || targetIndex === -1) return

    // Create new photos array with reordered items
    const newPhotos = [...photos]

    // If moving within same album
    if (draggedFromAlbumId === selectedAlbum?.id) {
      const albumPhotos = newPhotos.filter(p => p.albumId === draggedFromAlbumId)
      const [draggedPhoto] = albumPhotos.splice(draggedIndex, 1)
      albumPhotos.splice(targetIndex, 0, draggedPhoto)

      // Update the main photos array with reordered album photos
      const otherPhotos = newPhotos.filter(p => p.albumId !== draggedFromAlbumId)
      const updatedPhotos = [...otherPhotos, ...albumPhotos]
      setPhotos(updatedPhotos)

      console.log(`📸 Photo reordered within album: ${draggedPhotoId} moved to position ${targetIndex + 1}`)

      // Save photo order to backend
      const photoIds = albumPhotos.map(p => p.id)
      fetch(`${BACKEND_URL}/api/photos/${draggedFromAlbumId}/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ photoIds })
      })
        .then(res => res.json())
        .then(data => {
          if (data.error) {
            console.error('❌ Failed to save photo order:', data.error)
          } else {
            console.log('✅ Photo order saved to backend')
          }
        })
        .catch(err => console.error('❌ Error saving photo order:', err))
    } else if (selectedAlbum?.id) {
      // Moving photo from different album to selected album
      const draggedPhoto = newPhotos.find(p => p.id === draggedPhotoId)

      if (draggedPhoto) {
        // Extract actual source album and filename from photo ID
        const idParts = draggedPhoto.id.split('/')
        const actualSourceAlbum = idParts[0]
        const filename = idParts.slice(1).join('/')
        
        console.log(`📸 MOVE DEBUG:`)
        console.log(`  - Photo ID: ${draggedPhoto.id}`)
        console.log(`  - Source album from state: ${draggedFromAlbumId}`)
        console.log(`  - ACTUAL source album from ID: ${actualSourceAlbum}`)
        console.log(`  - Destination album: ${selectedAlbum.id}`)
        console.log(`  - Extracted filename: ${filename}`)
        console.log(`  - API call: ${BACKEND_URL}/api/photos/move`)

        // Call backend move endpoint to physically move the file in S3
        fetch(`${BACKEND_URL}/api/photos/move`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ 
            sourceAlbum: actualSourceAlbum,
            filename: filename,
            destinationAlbum: selectedAlbum.id 
          })
        })
          .then(res => res.json())
          .then(data => {
            if (data.error) {
              console.error('❌ Failed to move photo:', data.error)
              setUploadMessage(`❌ Failed to move photo: ${data.error}`)
              // Revert UI change on error
              if (authToken) loadAlbumsAndPhotos(authToken)
            } else {
              console.log('✅ Photo moved successfully in S3')
              setUploadMessage('✅ Photo moved successfully!')
              // Refresh photos to get updated state from backend
              if (authToken) loadAlbumsAndPhotos(authToken)
            }
          })
          .catch(err => {
            console.error('❌ Error moving photo:', err)
            setUploadMessage(`❌ Error moving photo: ${err.message}`)
            // Revert UI change on error
            if (authToken) loadAlbumsAndPhotos(authToken)
          })

        // Optimistically update UI
        const sourcePhotos = newPhotos.filter(p => p.id !== draggedPhotoId)
        draggedPhoto.albumId = selectedAlbum.id
        draggedPhoto.id = `${selectedAlbum.id}/${filename}` // Update photo ID with new album
        
        const targetAlbumPhotos = sourcePhotos.filter(p => p.albumId === selectedAlbum.id)
        targetAlbumPhotos.splice(targetIndex, 0, draggedPhoto)
        
        const otherPhotos = sourcePhotos.filter(p => p.albumId !== selectedAlbum.id)
        const updatedPhotos = [...otherPhotos, ...targetAlbumPhotos]
        setPhotos(updatedPhotos)
      }
    }

    setDraggedPhotoId(null)
    setDraggedFromAlbumId(null)
  }

  // Drop zone for moving photos to different album
  const handleAlbumDropZoneDragEnter = (e: React.DragEvent<HTMLDivElement>, albumId: string): void => {
    if (draggedPhotoId && draggedFromAlbumId && draggedFromAlbumId !== albumId) {
      e.preventDefault()
      setDragOverAlbumIdForPhoto(albumId)
    }
  }

  const handleAlbumDropZoneDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setDragOverAlbumIdForPhoto(null)
  }

  const handleAlbumDropZoneDrop = (e: React.DragEvent<HTMLDivElement>, targetAlbumId: string): void => {
    e.preventDefault()
    setDragOverAlbumIdForPhoto(null)

    if (!draggedPhotoId || !draggedFromAlbumId || draggedFromAlbumId === targetAlbumId) return

    // Move photo to different album
    const draggedPhoto = photos.find(p => p.id === draggedPhotoId)
    if (!draggedPhoto) return

    // Extract actual source album and filename from photo ID
    const idParts = draggedPhoto.id.split('/')
    const actualSourceAlbum = idParts[0]
    const filename = idParts.slice(1).join('/')
    
    console.log(`📸 MOVE DEBUG (album drop):`)
    console.log(`  - Photo ID: ${draggedPhoto.id}`)
    console.log(`  - Source album from state: ${draggedFromAlbumId}`)
    console.log(`  - ACTUAL source album from ID: ${actualSourceAlbum}`)
    console.log(`  - Destination album: ${targetAlbumId}`)
    console.log(`  - Extracted filename: ${filename}`)
    console.log(`  - API call: ${BACKEND_URL}/api/photos/move`)

    setDraggedPhotoId(null)
    setDraggedFromAlbumId(null)

    // Call backend move endpoint to physically move the file in S3
    fetch(`${BACKEND_URL}/api/photos/move`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ 
        sourceAlbum: actualSourceAlbum,
        filename: filename,
        destinationAlbum: targetAlbumId 
      })
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          console.error('❌ Failed to move photo:', data.error)
          setUploadMessage(`❌ Failed to move photo: ${data.error}`)
        } else {
          console.log('✅ Photo moved successfully in S3')
          setUploadMessage('✅ Photo moved successfully!')
          // Refresh photos to get updated state from backend
          if (authToken) loadAlbumsAndPhotos(authToken)
        }
      })
      .catch(err => {
        console.error('❌ Error moving photo:', err)
        setUploadMessage(`❌ Error moving photo: ${err.message}`)
      })

    // Optimistically update UI
    const updatedPhoto = { ...draggedPhoto, albumId: targetAlbumId, id: `${targetAlbumId}/${filename}` }
    const newPhotos = photos.map(p => p.id === draggedPhotoId ? updatedPhoto : p)
    setPhotos(newPhotos)
  }

  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '15px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          maxWidth: '400px',
          width: '100%',
          textAlign: 'center'
        }}>
          <h1 style={{ color: '#333', marginBottom: '10px' }}>🔐 Photo Manager</h1>
          <p style={{ color: '#666', marginBottom: '30px' }}>Enter password to access</p>

          <input
            type="password"
            placeholder="Password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleLogin()}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px',
              marginBottom: '15px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '1rem',
              opacity: isLoading ? 0.6 : 1
            }}
            autoFocus
          />

          {passwordError && <div style={{ color: '#e74c3c', marginBottom: '15px' }}>{passwordError}</div>}

          <button
            onClick={handleLogin}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px',
              background: isLoading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '1rem'
            }}
          >
            {isLoading ? '🔄 Authenticating...' : 'Unlock'}
          </button>
          
          <p style={{ marginTop: '20px', fontSize: '0.9rem', color: '#999' }}>
            Backend: www.eqsong.com (PHP + S3)
          </p>
        </div>
      </div>
    )
  }

  if (view === 'albums') {
    return (
      <div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '2px solid #eee'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>My Albums ({albums.length})</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setShowNewAlbumForm(!showNewAlbumForm)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              ➕ Create
            </button>
            <button
              onClick={() => {
                setView('statistics')
                if (authToken) {
                  loadStatistics(authToken)
                  loadDailyChart(authToken)
                }
              }}
              style={{
                padding: '10px 20px',
                background: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              📊 Statistics
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: '10px 20px',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {uploadMessage && (
          <div style={{
            marginBottom: '20px',
            padding: '12px',
            background: '#d4edda',
            color: '#155724',
            borderRadius: '8px'
          }}>
            {uploadMessage}
          </div>
        )}

        {showNewAlbumForm && (
          <div style={{
            background: '#f8f9fa',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '30px'
          }}>
            <h3 style={{ marginTop: 0 }}>Create New Album</h3>
            <input
              type="text"
              placeholder="Album name"
              value={newAlbumName}
              onChange={(e) => setNewAlbumName(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontFamily: 'inherit'
              }}
            />
            <textarea
              placeholder="Description"
              value={newAlbumDesc}
              onChange={(e) => setNewAlbumDesc(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontFamily: 'inherit'
              }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCreateAlbum}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Create
              </button>
              <button
                onClick={() => setShowNewAlbumForm(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="albums-grid">
          {localAlbums.map((album) => (
            <div key={album.id}>
              <div
                className="album-card"
                draggable
                onDragStart={(e) => handleDragStart(e, album.id)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, album.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, album.id)}
                style={{
                  cursor: draggedAlbumId === album.id ? 'grabbing' : 'grab',
                  opacity: draggedAlbumId === album.id ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  backgroundColor: dragOverAlbumId === album.id ? '#f0f0f0' : undefined,
                  borderColor: dragOverAlbumId === album.id ? '#667eea' : undefined
                }}
              >
              {editingAlbumId === album.id ? (
                <div style={{ padding: '20px', background: '#f8f9fa' }}>
                  <h3 style={{ marginTop: 0 }}>Edit Album</h3>
                  <input
                    type="text"
                    value={editingAlbumName}
                    onChange={(e) => setEditingAlbumName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      marginBottom: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <textarea
                    value={editingAlbumDesc}
                    onChange={(e) => setEditingAlbumDesc(e.target.value)}
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '8px',
                      marginBottom: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleSaveAlbum}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingAlbumId(null)}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    zIndex: 10
                  }}>
                    ⋮⋮ Drag to reorder
                  </div>
                  <div className="album-cover-image">
                    {album.coverImage && album.coverImage.toLowerCase().endsWith('.pdf') ? (
                      <div style={{
                        width: '100%',
                        height: '180px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                        color: 'white',
                        borderRadius: '8px'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '48px' }}>📄</div>
                          <div style={{ fontSize: '14px', marginTop: '6px' }}>{album.name}</div>
                        </div>
                      </div>
                    ) : (
                      <LazyImage src={album.coverImage} alt={album.name} />
                    )}
                  </div>
                  <div className="album-info">
                    <h3>{album.name}</h3>
                    <p className="album-description">{album.description}</p>
                    <div className="album-meta">
                      <span>📸 {album.photoCount} photos</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      marginTop: '12px'
                    }}>
                      <button
                        onClick={() => {
                          setSelectedAlbumId(album.id)
                          setView('photos')
                        }}
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}
                      >
                        📷 View
                      </button>
                      <button
                        onClick={() => handleToggleAlbumHidden(album.id)}
                        disabled={togglingAlbumId === album.id}
                        style={{
                          padding: '8px 12px',
                          background: album.hidden ? '#17a2b8' : '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: togglingAlbumId === album.id ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          opacity: togglingAlbumId === album.id ? 0.6 : 1
                        }}
                        title={album.hidden ? 'Show album in Gallery' : 'Hide album from Gallery'}
                      >
                        {album.hidden ? '👁️‍🗨️' : '👁️'}
                      </button>
                      <button
                        onClick={() => handleStartEditAlbum(album)}
                        style={{
                          padding: '8px 12px',
                          background: '#ffc107',
                          color: '#333',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteAlbum(album)}
                        style={{
                          padding: '8px 12px',
                          background: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </>
              )}
              </div>
              
              {/* Drop zone for moving photos to this album */}
              <div
                onDragEnter={(e) => handleAlbumDropZoneDragEnter(e, album.id)}
                onDragLeave={handleAlbumDropZoneDragLeave}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move' }}
                onDrop={(e) => handleAlbumDropZoneDrop(e, album.id)}
                style={{
                  minHeight: draggedPhotoId && draggedFromAlbumId && draggedFromAlbumId !== album.id ? '40px' : '0px',
                  backgroundColor: dragOverAlbumIdForPhoto === album.id ? '#e8f4f8' : 'transparent',
                  border: dragOverAlbumIdForPhoto === album.id ? '2px dashed #667eea' : '2px dashed transparent',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: dragOverAlbumIdForPhoto === album.id ? '#667eea' : '#999',
                  fontSize: '0.85rem',
                  fontWeight: dragOverAlbumIdForPhoto === album.id ? '600' : '400',
                  marginTop: '8px'
                }}
              >
                {dragOverAlbumIdForPhoto === album.id ? '📷 Drop photo here' : ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (view === 'photos' && selectedAlbum) {
    console.log(`📸 RENDERING PHOTOS VIEW for album: ${selectedAlbum.id}`)
    const albumPhotos = photos.filter(p => p.albumId === selectedAlbum.id)

    return (
      <div>
        <div style={{
          marginBottom: '20px',
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => { setView('albums'); setSelectedAlbumId(null) }}
            style={{
              padding: '10px 20px',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
          <button
            onClick={() => handleDeleteAlbum(selectedAlbum || undefined)}
            disabled={isLoading}
            type="button"
            style={{
              padding: '10px 20px',
              background: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              opacity: isLoading ? 0.5 : 1
            }}
          >
            🗑️ Delete Album
          </button>
          {selectedAlbum.id === 'featured-photos' && (
            <button
              onClick={() => void openHeroHeightModal()}
              disabled={isLoading}
              type="button"
              style={{
                padding: '10px 20px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                opacity: isLoading ? 0.5 : 1
              }}
            >
              📐 Adjust Hero Height
            </button>
          )}
        </div>

        <h2 style={{ color: '#333' }}>{selectedAlbum.name}</h2>
        <p style={{ color: '#666' }}>{selectedAlbum.description}</p>

        {uploadMessage && (
          <div style={{
            marginBottom: '15px',
            padding: '12px',
            background: '#d4edda',
            color: '#155724',
            borderRadius: '8px'
          }}>
            {uploadMessage}
          </div>
        )}

        <div
          style={{
            border: '2px dashed #667eea',
            borderRadius: '8px',
            padding: '40px',
            textAlign: 'center',
            background: '#f8f9fa',
            marginBottom: '30px',
            transition: 'all 0.3s ease',
            cursor: 'pointer'
          }}
          onDragOver={(e) => {
            e.preventDefault()
            e.currentTarget.style.background = '#e7e9f5'
            e.currentTarget.style.borderColor = '#764ba2'
          }}
          onDragEnter={(e) => {
            e.preventDefault()
            e.currentTarget.style.background = '#e7e9f5'
            e.currentTarget.style.borderColor = '#764ba2'
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.background = '#f8f9fa'
            e.currentTarget.style.borderColor = '#667eea'
          }}
          onDrop={(e) => {
            e.preventDefault()
            e.currentTarget.style.background = '#f8f9fa'
            e.currentTarget.style.borderColor = '#667eea'
            handleFileUpload(e.dataTransfer.files)
          }}
        >
          <p style={{ fontSize: '1.1rem', color: '#333', marginBottom: '15px' }}>📁 Drag photos & videos here or</p>
          <input
            type="file"
            multiple
            accept="image/*,video/*,.pdf,application/pdf"
            onChange={(e) => {
              handleFileUpload(e.target.files)
              // Clear the file input to prevent duplicate uploads if user selects the same files again
              e.target.value = ''
            }}
            id="file-input"
            style={{ display: 'none' }}
          />
          <button
            onClick={() => document.getElementById('file-input')?.click()}
            style={{
              padding: '10px 30px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            📂 Select Files (Images & Videos - Max 150MB each)
          </button>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <h3 style={{ color: '#333', margin: 0 }}>Photos ({albumPhotos.length}) {selectedPhotoIds.size > 0 && <span style={{ color: '#e74c3c', fontSize: '0.9rem' }}>- {selectedPhotoIds.size} selected</span>}</h3>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {selectedPhotoIds.size > 0 && (
              <>
                <button
                  onClick={() => setSelectedPhotoIds(new Set())}
                  style={{
                    background: '#95a5a6',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '0.85rem'
                  }}
                >
                  Clear
                </button>
                <button
                  onClick={handleDeleteMultiplePhotos}
                  disabled={isLoading}
                  style={{
                    background: '#e74c3c',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    color: 'white',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.85rem',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  🗑️ Delete ({selectedPhotoIds.size})
                </button>
              </>
            )}
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              background: '#f5f5f5',
              padding: '8px 12px',
              borderRadius: '6px'
            }}>
            <button
              onClick={() => setGalleryZoom(Math.max(50, galleryZoom - 20))}
              style={{
                background: '#667eea',
                border: 'none',
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s ease'
              }}
              title="Zoom Out Gallery"
              onMouseEnter={(e) => (e.currentTarget.style.background = '#5568d3')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#667eea')}
            >
              −
            </button>
            <span style={{ fontSize: '0.85rem', color: '#666', minWidth: '50px', textAlign: 'center', fontWeight: '500' }}>
              {galleryZoom}%
            </span>
            <button
              onClick={() => setGalleryZoom(Math.min(200, galleryZoom + 20))}
              style={{
                background: '#667eea',
                border: 'none',
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s ease'
              }}
              title="Zoom In Gallery"
              onMouseEnter={(e) => (e.currentTarget.style.background = '#5568d3')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#667eea')}
            >
              +
            </button>
            <button
              onClick={() => setGalleryZoom(100)}
              style={{
                background: '#95a5a6',
                border: 'none',
                borderRadius: '4px',
                width: '32px',
                height: '32px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s ease'
              }}
              title="Reset Gallery Zoom"
              onMouseEnter={(e) => (e.currentTarget.style.background = '#7f8c8d')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#95a5a6')}
            >
              ⊡
            </button>
            </div>
          </div>
        </div>

        {albumPhotos.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${Math.max(120, 200 * (galleryZoom / 100))}px, 1fr))`,
            gap: '20px',
            transition: 'grid-template-columns 0.3s ease'
          }}>
            {albumPhotos.map((photo) => (
              <div
                key={photo.id}
                draggable
                onDragStart={(e) => handlePhotoCardDragStart(e as any, photo.id, photo.albumId)}
                onDragOver={handlePhotoCardDragOver}
                onDragEnter={(e) => handlePhotoCardDragEnter(e as any, photo.id)}
                onDragLeave={handlePhotoCardDragLeave}
                onDrop={(e) => handlePhotoCardDrop(e as any, photo.id)}
                style={{
                  position: 'relative',
                  background: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
                  cursor: draggedPhotoId === photo.id ? 'grabbing' : 'grab',
                  opacity: draggedPhotoId === photo.id ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  backgroundColor: dragOverPhotoId === photo.id ? '#f0f0f0' : undefined,
                  borderColor: dragOverPhotoId === photo.id ? '#667eea' : undefined
                }}
              >
                {photo.type === 'video' ? (
                  <>
                    <LazyImage
                      src={photo.thumbnailUrl || photo.url}
                      alt={photo.title}
                      style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '2.5rem',
                      textShadow: '0 0 10px rgba(0,0,0,0.5)'
                    }}>
                      ▶️
                    </div>
                  </>
                ) : photo.type === 'pdf' ? (
                  <>
                    <LazyImage
                      src={photo.thumbnailUrl || photo.url}
                      alt={photo.title}
                      style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '2.5rem',
                      textShadow: '0 0 10px rgba(0,0,0,0.5)'
                    }}>
                      📄
                    </div>
                  </>
                ) : (
                  <LazyImage
                    src={photo.thumbnailUrl || photo.url}
                    alt={photo.title}
                    style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                  />
                )}
                <div style={{
                  position: 'absolute',
                  top: '5px',
                  left: '5px',
                  background: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  zIndex: 5
                }}>
                  ⋮⋮
                </div>
                <button
                  onClick={() => {
                    const newSelected = new Set(selectedPhotoIds)
                    if (newSelected.has(photo.id)) {
                      newSelected.delete(photo.id)
                    } else {
                      newSelected.add(photo.id)
                    }
                    setSelectedPhotoIds(newSelected)
                  }}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '85px',
                    background: selectedPhotoIds.has(photo.id) ? '#667eea' : 'rgba(0, 0, 0, 0.6)',
                    border: '2px solid white',
                    borderRadius: '4px',
                    width: '24px',
                    height: '24px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Select Photo"
                >
                  {selectedPhotoIds.has(photo.id) ? '✓' : ''}
                </button>
                <button
                  onClick={() => setMaximizedPhotoId(photo.id)}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '45px',
                    background: 'rgba(0, 0, 0, 0.6)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s ease'
                  }}
                  title="Maximize"
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)')}
                >
                  🔍
                </button>
                <button
                  onClick={() => setWatermarkingPhotoId(photo.id)}
                  style={{
                    position: 'absolute',
                    top: '45px',
                    right: '45px',
                    background: 'rgba(102, 126, 234, 0.8)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s ease'
                  }}
                  title="Add Watermark"
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(102, 126, 234, 1)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(102, 126, 234, 0.8)')}
                >
                  🔏
                </button>
                <button
                  onClick={() => void handleUndoWatermark(photo.id)}
                  disabled={isLoading}
                  style={{
                    position: 'absolute',
                    top: '85px',
                    right: '45px',
                    background: 'rgba(230, 126, 34, 0.8)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    color: 'white',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontSize: '1.2rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s ease',
                    opacity: isLoading ? 0.6 : 1
                  }}
                  title="Undo Watermark"
                  onMouseEnter={(e) => !isLoading && (e.currentTarget.style.background = 'rgba(230, 126, 34, 1)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(230, 126, 34, 0.8)')}
                >
                  ↩️
                </button>
                {selectedAlbum?.id === 'featured-photos' && photo.type !== 'video' && (
                  <button
                    onClick={() => {
                      const [xStr, yStr] = (photo.focalPoint || '50% 20%').split(' ')
                      setHeroFocalX(parseInt(xStr) || 50)
                      setHeroFocalY(parseInt(yStr) || 20)
                      setHeroPositionPhotoId(photo.id)
                    }}
                    style={{
                      position: 'absolute',
                      top: '125px',
                      right: '45px',
                      background: 'rgba(40, 167, 69, 0.85)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s ease'
                    }}
                    title="Adjust Hero Position"
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(40, 167, 69, 1)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(40, 167, 69, 0.85)')}
                  >
                    🎯
                  </button>
                )}
                <div style={{ padding: '12px' }}>
                  {renamingPhotoId === photo.id ? (
                    <div style={{ marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={renamingPhotoTitle}
                        onChange={(e) => setRenamingPhotoTitle(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            void handleRenamePhoto(photo.id)
                          }
                        }}
                        autoFocus
                        style={{
                          width: '100%',
                          padding: '6px',
                          border: '1px solid #667eea',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          marginBottom: '6px',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => void handleRenamePhoto(photo.id)}
                          disabled={isLoading}
                          style={{
                            flex: 1,
                            padding: '4px',
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setRenamingPhotoId(null)}
                          style={{
                            flex: 1,
                            padding: '4px',
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h4 
                        style={{ 
                          margin: '0 0 5px 0', 
                          color: '#333', 
                          fontSize: '0.95rem',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          backgroundColor: 'transparent',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        onClick={() => {
                          setRenamingPhotoId(photo.id)
                          setRenamingPhotoTitle(photo.title)
                        }}
                        title="Click to rename"
                      >
                        ✏️ {photo.title}
                      </h4>
                      <p style={{ margin: 0, color: '#999', fontSize: '0.8rem' }}>
                        {photo.caption}
                      </p>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#999'
          }}>
            <p>No photos yet. Upload some to get started!</p>
          </div>
        )}

        {draggedPhotoId && draggedFromAlbumId && draggedFromAlbumId === selectedAlbum?.id && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            border: '2px solid #ddd',
            borderRadius: '8px',
            padding: '15px 20px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            maxWidth: '80%',
            maxHeight: '120px',
            overflowY: 'auto'
          }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', color: '#666', fontWeight: '500' }}>
              Drop to move to another album:
            </label>
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              {albums && albums.filter(a => a.id !== selectedAlbum?.id).length > 0 ? (
                albums.filter(a => a.id !== selectedAlbum?.id).map(album => (
                  <div
                    key={album.id}
                    draggable="false"
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (draggedPhotoId && draggedFromAlbumId && draggedFromAlbumId !== album.id) {
                        const draggedPhoto = photos.find(p => p.id === draggedPhotoId)
                        if (draggedPhoto) {
                          // Extract actual source album and filename from photo ID
                          // Photo ID format: "albumId/filename"
                          const idParts = draggedPhoto.id.split('/')
                          const actualSourceAlbum = idParts[0]
                          const filename = idParts.slice(1).join('/')
                          
                          console.log(`📸 MOVE DEBUG (sidebar drop):`)
                          console.log(`  - Photo ID: ${draggedPhoto.id}`)
                          console.log(`  - Source album from state: ${draggedFromAlbumId}`)
                          console.log(`  - ACTUAL source album from ID: ${actualSourceAlbum}`)
                          console.log(`  - Destination album: ${album.id}`)
                          console.log(`  - Extracted filename: ${filename}`)
                          console.log(`  - API call: ${BACKEND_URL}/api/photos/move`)
                          
                          setDraggedPhotoId(null)
                          setDraggedFromAlbumId(null)
                          setDragOverAlbumIdForPhoto(null)
                          
                          // Call backend move endpoint to physically move the file in S3
                          fetch(`${BACKEND_URL}/api/photos/move`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${authToken}`
                            },
                            body: JSON.stringify({ 
                              sourceAlbum: actualSourceAlbum,
                              filename: filename,
                              destinationAlbum: album.id 
                            })
                          })
                            .then(async res => {
                              const data = await res.json()
                              console.log('Move response:', { status: res.status, data })
                              if (data.error) {
                                console.error('❌ Failed to move photo:', data)
                                setUploadMessage(`❌ ${data.details || data.error} (code: ${data.code || 'unknown'})`)
                              } else {
                                console.log('✅ Photo moved successfully in S3')
                                setUploadMessage('✅ Photo moved successfully!')
                                // Refresh photos to get updated state from backend
                                if (authToken) loadAlbumsAndPhotos(authToken)
                              }
                            })
                            .catch(err => {
                              console.error('❌ Error moving photo:', err)
                              setUploadMessage(`❌ Error moving photo: ${err.message}`)
                            })

                          // Optimistically update UI
                          const updatedPhoto = { ...draggedPhoto, albumId: album.id, id: `${album.id}/${filename}` }
                          const newPhotos = photos.map(p => p.id === draggedPhotoId ? updatedPhoto : p)
                          setPhotos(newPhotos)
                        }
                      }
                    }}
                    onMouseEnter={() => setDragOverAlbumIdForPhoto(album.id)}
                    onMouseLeave={() => setDragOverAlbumIdForPhoto(null)}
                    style={{
                      padding: '8px 12px',
                      background: dragOverAlbumIdForPhoto === album.id ? '#e8f4f8' : '#f5f5f5',
                      border: dragOverAlbumIdForPhoto === album.id ? '2px solid #3498db' : '1px solid #ddd',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: dragOverAlbumIdForPhoto === album.id ? '600' : '500',
                      color: '#333',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap'
                    }}
                    title={album.name}
                  >
                    {dragOverAlbumIdForPhoto === album.id ? '✓ ' : ''}📁 {album.name}
                  </div>
                ))
              ) : (
                <p style={{ color: '#999', fontSize: '0.85rem', margin: 0 }}>No other albums</p>
              )}
            </div>
          </div>
        )}

        {maximizedPhoto && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isModalMinimized ? 'transparent' : 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: isModalMinimized ? 'flex-end' : 'center',
            justifyContent: isModalMinimized ? 'flex-end' : 'center',
            zIndex: 2000,
            padding: '20px',
            pointerEvents: isModalMinimized ? 'none' : 'auto'
          }}
          onClick={() => !isModalMinimized && setMaximizedPhotoId(null)}
          >
            <div style={{
              position: isModalMinimized ? 'fixed' : 'relative',
              bottom: isModalMinimized ? '20px' : 'auto',
              right: isModalMinimized ? '20px' : 'auto',
              background: 'white',
              borderRadius: '8px',
              width: isModalMinimized ? '300px' : '90vw',
              height: isModalMinimized ? 'auto' : '90vh',
              maxWidth: isModalMinimized ? '300px' : '90vw',
              maxHeight: isModalMinimized ? 'auto' : '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.3s ease',
              pointerEvents: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 15px',
                borderBottom: '1px solid #eee',
                background: '#f5f5f5',
                borderRadius: '8px 8px 0 0'
              }}>
                <h4 style={{ margin: 0, color: '#333', fontSize: '0.95rem', flex: 1 }}>
                  {isModalMinimized ? maximizedPhoto.title.substring(0, 20) + '...' : maximizedPhoto.title}
                </h4>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {!isModalMinimized && maximizedPhoto.type !== 'video' && maximizedPhoto.type !== 'pdf' && (
                    <>
                      <button
                        onClick={() => setPhotoZoom(Math.max(50, photoZoom - 20))}
                        style={{
                          background: '#667eea',
                          border: 'none',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s ease'
                        }}
                        title="Zoom Out"
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#5568d3')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#667eea')}
                      >
                        −
                      </button>
                      <span style={{ fontSize: '0.85rem', color: '#666', minWidth: '45px', textAlign: 'center' }}>
                        {photoZoom}%
                      </span>
                      <button
                        onClick={() => setPhotoZoom(Math.min(300, photoZoom + 20))}
                        style={{
                          background: '#667eea',
                          border: 'none',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s ease'
                        }}
                        title="Zoom In"
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#5568d3')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#667eea')}
                      >
                        +
                      </button>
                      <button
                        onClick={() => setPhotoZoom(100)}
                        style={{
                          background: '#95a5a6',
                          border: 'none',
                          borderRadius: '4px',
                          width: '32px',
                          height: '32px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s ease'
                        }}
                        title="Reset Zoom"
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#7f8c8d')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#95a5a6')}
                      >
                        1:1
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setIsModalMinimized(!isModalMinimized)}
                    style={{
                      background: '#f39c12',
                      border: 'none',
                      borderRadius: '4px',
                      width: '32px',
                      height: '32px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s ease'
                    }}
                    title={isModalMinimized ? 'Expand' : 'Minimize'}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#e67e22')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#f39c12')}
                  >
                    {isModalMinimized ? '▲' : '▼'}
                  </button>
                  <button
                    onClick={() => { setMaximizedPhotoId(null); setPhotoZoom(100) }}
                    style={{
                      background: '#e74c3c',
                      border: 'none',
                      borderRadius: '4px',
                      width: '32px',
                      height: '32px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s ease'
                    }}
                    title="Close"
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#c0392b')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#e74c3c')}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {!isModalMinimized && (
                <>
                  <div style={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 0,
                    background: '#fafafa'
                  }}>
                    {maximizedPhoto.type === 'video' ? (
                      <video
                        src={maximizedPhoto.url}
                        controls
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain'
                        }}
                        autoPlay
                      />
                    ) : maximizedPhoto.type === 'pdf' ? (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <PDFViewer
                          url={maximizedPhoto.url}
                          title={maximizedPhoto.title}
                          isFullscreen={false}
                        />
                      </div>
                    ) : (
                      <img
                        src={maximizedPhoto.url}
                        alt={maximizedPhoto.title}
                        style={{
                          width: `${photoZoom}%`,
                          maxWidth: 'none',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          transition: 'width 0.2s ease'
                        }}
                      />
                    )}
                  </div>

                  <div style={{
                    padding: '15px',
                    borderTop: '1px solid #eee',
                    background: '#f9f9f9'
                  }}>
                    <h3 style={{ margin: '0 0 6px 0', color: '#333', fontSize: '0.95rem' }}>
                      {maximizedPhoto.title}
                    </h3>
                    <p style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.9rem' }}>
                      {maximizedPhoto.caption}
                    </p>
                    <p style={{ margin: 0, color: '#999', fontSize: '0.8rem' }}>
                      Uploaded: {new Date(maximizedPhoto.uploadDate).toLocaleDateString()}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Watermark Modal */}
        {watermarkingPhotoId && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}>
              <h2 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '1.5rem' }}>
                🔏 Add Watermark
              </h2>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '600' }}>
                  Watermark Text:
                </label>
                <input
                  type="text"
                  value={watermarkText}
                  onChange={(e) => setWatermarkText(e.target.value)}
                  placeholder="Enter watermark text"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '600' }}>
                  Position:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  {(['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center'] as const).map((pos) => (
                    <button
                      key={pos}
                      onClick={() => setWatermarkPosition(pos)}
                      style={{
                        padding: '12px',
                        background: watermarkPosition === pos ? '#667eea' : '#f0f0f0',
                        border: '2px solid ' + (watermarkPosition === pos ? '#667eea' : '#ddd'),
                        borderRadius: '6px',
                        cursor: 'pointer',
                        color: watermarkPosition === pos ? 'white' : '#333',
                        fontWeight: watermarkPosition === pos ? '600' : '400',
                        transition: 'all 0.2s ease',
                        textTransform: 'capitalize',
                        fontSize: '0.85rem'
                      }}
                    >
                      {pos.replace('-', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '600' }}>
                  Font Size: {watermarkSize}px
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={watermarkSize}
                  onChange={(e) => setWatermarkSize(parseInt(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#555', fontWeight: '600' }}>
                  Opacity: {(watermarkOpacity * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={watermarkOpacity}
                  onChange={(e) => setWatermarkOpacity(parseFloat(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    console.log('🎬 Apply button clicked!', { watermarkingPhotoId, watermarkText, watermarkSize, watermarkPosition, watermarkOpacity })
                    if (watermarkingPhotoId) {
                      void handleApplyWatermark(watermarkingPhotoId)
                    } else {
                      setUploadMessage('⚠️ No photo selected')
                      setTimeout(() => setUploadMessage(''), 3000)
                    }
                  }}
                  disabled={isLoading || !watermarkingPhotoId}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: watermarkingPhotoId ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoading || !watermarkingPhotoId ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    opacity: isLoading || !watermarkingPhotoId ? 0.6 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => !isLoading && watermarkingPhotoId && (e.currentTarget.style.background = '#218838')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = watermarkingPhotoId ? '#28a745' : '#ccc')}
                >
                  {isLoading ? '⏳ Applying...' : '✓ Apply Watermark'}
                </button>
                <button
                  onClick={() => setWatermarkingPhotoId(null)}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {heroPositionPhotoId && (() => {
          const heroPhoto = photos.find(p => p.id === heroPositionPhotoId)
          return (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000
            }}>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '30px',
                maxWidth: '600px',
                width: '90%',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
              }}>
                <h2 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '1.5rem' }}>
                  🎯 Adjust Hero Position
                </h2>
                <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '0.9rem' }}>
                  Click on the photo to mark the point that should stay centered when it's shown full-width on the home page.
                </p>

                <div
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect()
                    const x = Math.round(((e.clientX - rect.left) / rect.width) * 100)
                    const y = Math.round(((e.clientY - rect.top) / rect.height) * 100)
                    setHeroFocalX(Math.min(100, Math.max(0, x)))
                    setHeroFocalY(Math.min(100, Math.max(0, y)))
                  }}
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '320px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'crosshair',
                    background: '#000',
                    marginBottom: '16px'
                  }}
                >
                  {heroPhoto && (
                    <img
                      src={heroPhoto.url}
                      alt={heroPhoto.title}
                      style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                    />
                  )}
                  <div style={{
                    position: 'absolute',
                    left: `${heroFocalX}%`,
                    top: `${heroFocalY}%`,
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: '3px solid #28a745',
                    background: 'rgba(40, 167, 69, 0.3)',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    boxShadow: '0 0 0 2px white'
                  }} />
                </div>

                <p style={{ margin: '0 0 8px 0', color: '#555', fontSize: '0.85rem', fontWeight: '600' }}>
                  Live Preview — how it will look full-width on the home page:
                </p>
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '110px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: '#000',
                  marginBottom: '20px',
                  border: '1px solid #ddd'
                }}>
                  {heroPhoto && (
                    <img
                      src={heroPhoto.url}
                      alt={heroPhoto.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: `${heroFocalX}% ${heroFocalY}%`
                      }}
                    />
                  )}
                </div>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                  <label style={{ flex: 1, fontSize: '0.85rem', color: '#555' }}>
                    Horizontal: {heroFocalX}%
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={heroFocalX}
                      onChange={(e) => setHeroFocalX(parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                  </label>
                  <label style={{ flex: 1, fontSize: '0.85rem', color: '#555' }}>
                    Vertical: {heroFocalY}%
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={heroFocalY}
                      onChange={(e) => setHeroFocalY(parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => void handleSaveHeroPosition(heroPositionPhotoId)}
                    disabled={isLoading}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      opacity: isLoading ? 0.6 : 1
                    }}
                  >
                    {isLoading ? '⏳ Saving...' : '✓ Save Position'}
                  </button>
                  <button
                    onClick={() => setHeroPositionPhotoId(null)}
                    disabled={isLoading}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '1rem',
                      fontWeight: '600',
                      opacity: isLoading ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

        {heroHeightModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
            }}>
              <h2 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '1.5rem' }}>
                📐 Adjust Hero Height
              </h2>
              <p style={{ margin: '0 0 20px 0', color: '#666', fontSize: '0.9rem' }}>
                Controls how tall the full-width hero window is on the home page.
              </p>

              <div style={{
                width: '100%',
                height: `${Math.round(heroHeightValue / 4)}px`,
                maxHeight: '260px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                marginBottom: '20px',
                transition: 'height 0.15s ease'
              }}>
                Preview: {heroHeightValue}px
              </div>

              <label style={{ display: 'block', marginBottom: '20px', fontSize: '0.85rem', color: '#555' }}>
                Height: {heroHeightValue}px
                <input
                  type="range"
                  min="300"
                  max="1200"
                  step="10"
                  value={heroHeightValue}
                  onChange={(e) => setHeroHeightValue(parseInt(e.target.value))}
                  style={{ width: '100%' }}
                />
              </label>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => void handleSaveHeroHeight()}
                  disabled={heroHeightLoading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: heroHeightLoading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    opacity: heroHeightLoading ? 0.6 : 1
                  }}
                >
                  {heroHeightLoading ? '⏳ Saving...' : '✓ Save Height'}
                </button>
                <button
                  onClick={() => setHeroHeightModalOpen(false)}
                  disabled={heroHeightLoading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: heroHeightLoading ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600',
                    opacity: heroHeightLoading ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  if (view === 'statistics') {
    const totalViews = statisticsData.reduce((sum, item) => sum + item.count, 0)
    
    return (<>
      <div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '2px solid #eee'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>📊 Review Statistics</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setView('albums')}
              style={{
                padding: '10px 20px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              ← Back to Albums
            </button>
            <button
              onClick={() => authToken && (loadStatistics(authToken), loadDailyChart(authToken))}
              disabled={statisticsLoading}
              style={{
                padding: '10px 20px',
                background: statisticsLoading ? '#95a5a6' : '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: statisticsLoading ? 'not-allowed' : 'pointer',
                fontWeight: '600'
              }}
            >
              {statisticsLoading ? '🔄 Loading...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {/* Total views summary */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '3rem' }}>👁 {totalViews.toLocaleString()}</h1>
          <p style={{ margin: 0, fontSize: '1.2rem', opacity: 0.9 }}>
            Total Photo Reviews (excluding self)
          </p>
          <p style={{ margin: '10px 0 0 0', fontSize: '1rem', opacity: 0.8 }}>
            Across {statisticsData.length} photos in {new Set(statisticsData.map(d => d.albumId)).size} albums
          </p>
        </div>

        {/* Review trend chart (selectable period, up to 2 years) */}
        <div style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          padding: '20px',
          marginBottom: '30px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '16px'
          }}>
            <h3 style={{ color: '#333', margin: 0 }}>📈 Views Over the Last {CHART_RANGE_OPTIONS.find(o => o.days === dailyChartRangeDays)?.label || `${dailyChartRangeDays} Days`}</h3>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {CHART_RANGE_OPTIONS.map(option => (
                <button
                  key={option.days}
                  onClick={() => {
                    setDailyChartRangeDays(option.days)
                    if (authToken) loadDailyChart(authToken, option.days)
                  }}
                  disabled={dailyChartLoading}
                  style={{
                    padding: '6px 14px',
                    background: dailyChartRangeDays === option.days ? '#667eea' : '#f1f3f5',
                    color: dailyChartRangeDays === option.days ? 'white' : '#495057',
                    border: 'none',
                    borderRadius: '20px',
                    cursor: dailyChartLoading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.85rem'
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          {dailyChartLoading ? (
            <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>Loading chart...</p>
          ) : dailyChartData.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No chart data yet.</p>
          ) : (() => {
            const chartWidth = 900
            const chartHeight = 180
            const labelHeight = 40
            const maxCount = Math.max(1, ...dailyChartData.map(d => d.count))
            const slotWidth = chartWidth / dailyChartData.length
            const barWidth = Math.max(4, slotWidth * 0.6)
            return (
              <svg viewBox={`0 0 ${chartWidth} ${chartHeight + labelHeight}`} width="100%" style={{ display: 'block' }}>
                {/* Baseline */}
                <line x1={0} y1={chartHeight} x2={chartWidth} y2={chartHeight} stroke="#dee2e6" strokeWidth={1} />
                {dailyChartData.map((day, index) => {
                  const barHeight = (day.count / maxCount) * (chartHeight - 10)
                  const x = index * slotWidth + (slotWidth - barWidth) / 2
                  const y = chartHeight - barHeight
                  const isSelected = selectedChartDate === day.date
                  const showLabel = index % 5 === 0 || index === dailyChartData.length - 1
                  return (
                    <g key={day.date}>
                      <rect
                        x={x}
                        y={y}
                        width={barWidth}
                        height={Math.max(barHeight, day.count > 0 ? 2 : 0)}
                        rx={2}
                        fill={isSelected ? '#764ba2' : '#667eea'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedChartDate(isSelected ? null : day.date)}
                      >
                        <title>{`${day.label}: ${day.count} view(s)`}</title>
                      </rect>
                      {showLabel && (
                        <text
                          x={index * slotWidth + slotWidth / 2}
                          y={chartHeight + 20}
                          fontSize={12}
                          fill="#6c757d"
                          textAnchor="middle"
                        >
                          {day.label}
                        </text>
                      )}
                    </g>
                  )
                })}
              </svg>
            )
          })()}

          {selectedChartDate && (
            <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '16px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>
                Viewed on {new Date(selectedChartDate).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
                {' '}({(dailyChartDetails[selectedChartDate] || []).length} item{(dailyChartDetails[selectedChartDate] || []).length === 1 ? '' : 's'})
              </h4>
              {(dailyChartDetails[selectedChartDate] || []).length === 0 ? (
                <p style={{ color: '#999' }}>No items recorded for this day.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                  {(dailyChartDetails[selectedChartDate] || []).map(item => (
                    <div key={item.id} style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#f0f0f0',
                          position: 'relative',
                          cursor: 'pointer'
                        }}
                        onClick={() => setPreviewPhoto(item)}
                        title="Click to view full size"
                      >
                        {item.type === 'pdf' ? (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem'
                          }}>
                            📄
                          </div>
                        ) : (
                          <LazyImage
                            src={item.thumbnailUrl || item.url}
                            alt={item.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.8rem',
                        color: '#666',
                        marginTop: '4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {item.title}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Individual photo statistics */}
        {statisticsLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <p>Loading statistics...</p>
          </div>
        ) : statisticsData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <p>No review data yet. Photos need to be viewed by visitors to collect statistics.</p>
          </div>
        ) : (
          <div>
            <h3 style={{ color: '#333', marginBottom: '20px' }}>
              Photo Review Details ({statisticsData.length} photos)
            </h3>
            <div style={{
              background: 'white',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              {/* Table header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '50px 60px 1fr 200px 120px',
                padding: '15px 20px',
                background: '#f8f9fa',
                fontWeight: '600',
                color: '#495057',
                borderBottom: '2px solid #dee2e6'
              }}>
                <div>#</div>
                <div>Thumb</div>
                <div>Photo</div>
                <div>Album</div>
                <div style={{ textAlign: 'right' }}>Views</div>
              </div>
              
              {/* Table rows */}
              {statisticsData.map((item, index) => {
                const matchedPhoto = photos.find(p => p.albumId === item.albumId && getFilenameFromUrl(p.url) === item.filename)
                return (
                <div
                  key={`${item.albumId}-${item.filename}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '50px 60px 1fr 200px 120px',
                    padding: '15px 20px',
                    alignItems: 'center',
                    borderBottom: index < statisticsData.length - 1 ? '1px solid #dee2e6' : 'none',
                    transition: 'background 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ color: '#6c757d', fontWeight: '600' }}>
                    {index + 1}
                  </div>
                  <div
                    style={{ width: '48px', height: '48px', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#f0f0f0', cursor: matchedPhoto ? 'pointer' : 'default' }}
                    onClick={(e) => { e.stopPropagation(); if (matchedPhoto) setPreviewPhoto(matchedPhoto) }}
                    title={matchedPhoto ? 'Click to view full size' : undefined}
                  >
                    {matchedPhoto?.type === 'pdf' ? (
                      <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                        📄
                      </div>
                    ) : matchedPhoto ? (
                      <LazyImage
                        src={matchedPhoto.thumbnailUrl || matchedPhoto.url}
                        alt={matchedPhoto.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : null}
                  </div>
                  <div style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: '#212529'
                  }}>
                    {item.filename}
                  </div>
                  <div style={{ color: '#6c757d' }}>
                    {item.albumName}
                  </div>
                  <div style={{
                    textAlign: 'right',
                    fontWeight: '600',
                    color: '#667eea',
                    fontSize: '1.1rem'
                  }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); void loadReviewEvents(item.albumId, item.filename) }}
                      style={{ background: 'transparent', border: 'none', color: '#667eea', cursor: 'pointer', fontWeight: 600, fontSize: '1.1rem' }}
                      title="View detailed viewer timestamps"
                    >
                      {item.count.toLocaleString()}
                    </button>
                  </div>
                </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
      {eventsModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }} onClick={() => setEventsModalOpen(false)}>
          <div style={{ width: '90%', maxWidth: '720px', background: 'white', borderRadius: '8px', padding: '20px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0 }}>Viewer Timestamps</h3>
              <div>
                <button onClick={() => setEventsModalOpen(false)} style={{ padding: '6px 10px', borderRadius: '4px', background: '#e74c3c', color: 'white', border: 'none' }}>Close</button>
              </div>
            </div>
            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              {eventsLoading ? (
                <p>Loading...</p>
              ) : eventsForPhoto.length === 0 ? (
                <p style={{ color: '#666' }}>No viewer timestamps recorded for this photo.</p>
              ) : (
                <>
                  {(() => {
                    const byYear: Record<string, Record<string, number>> = {}
                    eventsForPhoto.forEach(ts => {
                      const d = new Date(ts)
                      const year = String(d.getFullYear())
                      const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                      byYear[year] = byYear[year] || {}
                      byYear[year][date] = (byYear[year][date] || 0) + 1
                    })
                    const years = Object.keys(byYear).sort((a, b) => Number(b) - Number(a))
                    return (
                      <div style={{ marginBottom: '16px', background: '#f8f9fa', borderRadius: '6px', padding: '12px 16px' }}>
                        <h4 style={{ margin: '0 0 8px 0' }}>Summary by Year</h4>
                        {years.map(year => (
                          <div key={year} style={{ marginBottom: '8px' }}>
                            <strong>{year}</strong> — {Object.values(byYear[year]).reduce((a, b) => a + b, 0)} view(s)
                            <div style={{ color: '#666', fontSize: '0.9em', marginTop: '2px' }}>
                              {Object.entries(byYear[year])
                                .map(([date, count]) => count > 1 ? `${date} (×${count})` : date)
                                .join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  })()}
                  <ol>
                    {eventsForPhoto.map((ts, i) => (
                      <li key={i} style={{ marginBottom: '6px' }}>{new Date(ts).toLocaleString()}</li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {previewPhoto && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3100 }}
          onClick={() => setPreviewPhoto(null)}
        >
          <button
            onClick={() => setPreviewPhoto(null)}
            style={{ position: 'absolute', top: '20px', right: '20px', fontSize: '2rem', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}
            title="Close"
          >
            ✕
          </button>
          <div style={{ width: '90%', height: '90%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
            {previewPhoto.type === 'pdf' ? (
              <div style={{ width: '100%', height: '100%', backgroundColor: '#f5f5f5', borderRadius: '8px', overflow: 'hidden' }}>
                <PDFViewer url={previewPhoto.url} title={previewPhoto.title} isFullscreen={false} />
              </div>
            ) : previewPhoto.type === 'video' ? (
              <video src={previewPhoto.url} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            ) : (
              <img src={previewPhoto.url} alt={previewPhoto.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            )}
          </div>
        </div>
      )}
    </>
    )
  }

  return null
}

export default PhotoManager
